import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { TopBarRedesigned, AdditionalActionsIconType } from 'components/TopBarRedesigned'
import DentistSideMenu from 'components/DentistSideMenu'
import FixedLoader from 'components/FixedLoader/FixedLoader'
import FixedError from 'components/FixedError/FixedError'
import DefaultButton from 'components/Buttons/DefaultButton'
import ReferralCodeCard from 'components/ReferralCodeCard'
import { usePersistedApi } from 'hooks'
import entities from 'reduxStore/entityKeys'
import Api from 'api'
import { Location as ApiLocation } from 'components/BillingLocationSelector'

import {
  PageContainer, Inner, Header, HeaderRight, AddBtn, SearchInput,
  List, Card, Info, Pin, Details, Address, Meta, Badge, Actions, ViewBtn,
  EmptyState, EmptyIcon, EmptyTitle, EmptySubtitle, Responsive
} from './LocationsListPage.styles'
import {
  getRevenueCustomerId,
  getRevenuePaymentInfoStatus,
  hasOfficeRevenuePaymentInfo,
  isUSLocation,
} from 'utils/paymentPurpose'

type ListLocation = ApiLocation & { completedJobs?: number }

const getCcBadgeVariant = (loc: ListLocation): 'active' | 'failed' | 'cancelled' | 'none' => {
  if (!getRevenueCustomerId(loc)) return 'none';
  if (hasOfficeRevenuePaymentInfo(loc)) return 'active';

  const sharedCardStatus = Number((loc as any).isCardValid);
  const revenueStatus = Number(getRevenuePaymentInfoStatus(loc));
  const hasRevenueStatus = getRevenuePaymentInfoStatus(loc) !== undefined && getRevenuePaymentInfoStatus(loc) !== null;
  if (sharedCardStatus === -2 || revenueStatus === -2) return 'cancelled';
  if (
    sharedCardStatus === 0
    || sharedCardStatus === -1
    || revenueStatus === 0
    || revenueStatus === -1
    || (isUSLocation(loc) && !hasRevenueStatus)
  ) return 'failed';
  return 'none';
}

export default function LocationsListPage() {
  const history = useHistory()
  const [searchQuery, setSearchQuery] = useState('')

  const [locations, locationsLoading, locationsError, getLocations] = usePersistedApi<{ } , ListLocation[]>({
    apiMethod: Api.getLocations,
    entityName: entities.dentistBillingLocations,
    initialValue: [],
  })


  useEffect(() => { getLocations({}) }, [getLocations])

  // HYBRID: Pure React navigation instead of exitReact
  const onHome = useCallback(() => {
    history.push({ pathname: 'dentistHome' });
  }, [history])
  
  const onAdd = useCallback(() => {
    history.push({ pathname: 'locationAdd' });
  }, [history])

  const onView = useCallback((locationId: number) => {
    history.push({ pathname: 'locationDetails', state: { locationId } });
  }, [history])


  const formatted = useMemo(() => (locations || []).map(l => ({
    ...l,
    fullAddress: `${l.practiceName ? l.practiceName + ' - ' : ''}${l.address}`
  })), [locations])

  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) return formatted
    const query = searchQuery.toLowerCase()
    return formatted.filter(loc => 
      loc.fullAddress.toLowerCase().includes(query) ||
      loc.city?.toLowerCase().includes(query) ||
      loc.province?.toLowerCase().includes(query) ||
      loc.practiceName?.toLowerCase().includes(query)
    )
  }, [formatted, searchQuery])

  // T2-5854: hamburger trigger in the page header opens the DO side menu
  // (Home / Locations / Invoices / Hire Staff / Support / Log Out).
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false)

  return (
    <PageContainer>
      <TopBarRedesigned
        title={'Locations'}
        onBack={onHome}
        additionalActionsIconType={AdditionalActionsIconType.menu}
        onAdditionalActionsButtonClick={() => setIsSideMenuOpen(true)}
      />
      <DentistSideMenu isOpen={isSideMenuOpen} onClose={() => setIsSideMenuOpen(false)} />
      <Inner>
        <Responsive />
        {/* T2-5090: account-level referral code + free placement credit balance (shown once). */}
        <ReferralCodeCard audience="office" />
        <Header>
          <SearchInput
            type="text"
            placeholder="Search locations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search locations"
          />
          <HeaderRight>
            <AddBtn className="no-fastclick" aria-label="Add Location" onClick={onAdd}><span>+</span><span>Add Location</span></AddBtn>
          </HeaderRight>
        </Header>

        {(!formatted || formatted.length === 0) && !locationsLoading ? (
          <EmptyState>
            <EmptyIcon>📭</EmptyIcon>
            <EmptyTitle>No locations yet</EmptyTitle>
            <EmptySubtitle>Add your first office location to get started.</EmptySubtitle>
            <DefaultButton onClick={onAdd}>Add Location</DefaultButton>
          </EmptyState>
        ) : filteredLocations.length === 0 ? (
          <EmptyState>
            <EmptyIcon>🔍</EmptyIcon>
            <EmptyTitle>No matching locations</EmptyTitle>
            <EmptySubtitle>Try a different search term.</EmptySubtitle>
          </EmptyState>
        ) : (
          <List>
            {filteredLocations.map((loc, idx) => (
              <Card key={loc.id} index={idx} aria-label={`Location ${loc.fullAddress}`}>
                <Info>
                  <Pin>📍</Pin>
                  <Details>
                    <Address>{loc.fullAddress}</Address>
                    <Meta>
                      <span>{(loc as any).completedJobs ?? 0} completed shifts</span>
                      <Badge variant={getCcBadgeVariant(loc)}>
                        {getCcBadgeVariant(loc) === 'active' && 'CC: ACTIVE'}
                        {getCcBadgeVariant(loc) === 'failed' && 'CC: FAILED'}
                        {getCcBadgeVariant(loc) === 'cancelled' && 'CC: CANCELLED'}
                        {getCcBadgeVariant(loc) === 'none' && 'CC: NONE'}
                      </Badge>
                    </Meta>
                  </Details>
                </Info>
                <Actions>
                  <ViewBtn className="no-fastclick" aria-label={`View ${loc.fullAddress}`} onClick={() => onView(loc.id)}>View →</ViewBtn>
                </Actions>
              </Card>
            ))}
          </List>
        )}
      </Inner>
      <FixedLoader loading={locationsLoading} />
      <FixedError error={locationsError} />
    </PageContainer>
  )
}
